<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.remooc_category_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="handleAddRemoocCategory">
                {{ $t('labels.remoocModule.remoocCategory.add') }}
            </hts-form-button>
        </div>
        <div class="body">
            <hts-data-table
                ref="dataTables"
                :headers="remoocCategoryHeaders"
                :server="apiListRemoocCategory"
                :use-archive="apiArchive"
                :use-restore="apiRestore"
                use-selection
                store-name="vehicleDataTable"
                store-key="VehicleRemoocCategory" />
        </div>
        <add-remooc-category
            ref="showDrawerAddRemoocCategory"
            @refresh-tables="refreshTables()" />
        <edit-remooc-category
            ref="showDrawerEditRemoocCategory"
            :info-remooc-category="remoocCategoryIsSelectedForEdit"
            @refresh-tables="refreshTables()" />
        <detail-remooc-category
            ref="showDrawerDetailRemoocCategory"
            :info-remooc-category="remoocCategoryIsSelectedForEdit"
            @refresh-tables="refreshTables()" />
        <hts-modal
            :show="showPopupDeleteRemoocCategory"
            :title="$t('labels.remoocModule.remoocCategory.delete')"
            type="confirm"
            :content="$t('labels.remoocModule.remoocCategory.confirm')"
            @hide="closePopupDeleteRemoocCategory"
            @confirm="actionDeleteRemoocCategory" />
    </div>
</template>

<script lang="ts" setup>
import Actions from './ignore/actions.vue'
import AddRemoocCategory from './ignore/remooc-category/add-remooc-category.vue'
import DetailRemoocCategory from './ignore/remooc-category/detail.vue'
import EditRemoocCategory from './ignore/remooc-category/edit-remooc-category.vue'
import { checkPermission } from '@/utils/permission-check'
import layoutPermissions from '@/data/permissions/layout-permissions'

const { $formatters } = useNuxtApp()

const showPopupDeleteRemoocCategory = ref(false)
const remoocCategoryIsSelectedForDelete = ref('')

const apiListRemoocCategory = $url.vehicle.remoocCategory.getList

const showDrawerAddRemoocCategory: any = ref()
const showDrawerEditRemoocCategory: any = ref()
const showDrawerDetailRemoocCategory: any = ref()

const remoocCategoryIsSelectedForEdit = reactive({})
const dataTables = ref()

const handleEditRemoocCategory = (remoocCategory: any) => {
  if (!remoocCategory) {
    return null
  }
  Object.assign(remoocCategoryIsSelectedForEdit, { ...remoocCategory })
  if (showDrawerEditRemoocCategory.value) {
    showDrawerEditRemoocCategory.value.openEditRemoocCategory()
  }
}

const handleViewDetail = (remoocCategory: any) => {
  if (!remoocCategory) {
    return null
  }

  Object.assign(remoocCategoryIsSelectedForEdit, { ...remoocCategory })
  if (showDrawerDetailRemoocCategory.value) {
    showDrawerDetailRemoocCategory.value.openEditRemoocCategory()
  }
}

const handleDeleteRemoocCategory = (remoocCategory: any) => {
  if (!remoocCategory) {
    return null
  }
  showPopupDeleteRemoocCategory.value = true
  remoocCategoryIsSelectedForDelete.value = remoocCategory._id
}

const remoocCategoryHeaders = [
  {
    field: 'type',
    headerName: $t('labels.remoocModule.containerCapacity'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      switch (params.data.type) {
      case 20:
        return $t('labels.vehicleModule.20ft')
      case 40:
        return $t('labels.vehicleModule.40ft')
      case 45:
        return $t('labels.vehicleModule.45ft')
      default:
        return ''
      }
    }
  },
  {
    field: 'wheelNumber',
    label: 'wheelNumber',
    headerName: $t('labels.vehicleModule.category.wheelNumber'),
    sortable: true
  },
  {
    field: 'priceTires',
    label: 'priceTires',
    headerName: $t('labels.vehicleModule.category.priceTires'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.priceTires)
    }
  },
  {
    field: 'depreciationTires',
    label: 'depreciationTires',
    headerName: $t('labels.vehicleModule.category.depreciationTires'),
    sortable: true
  },
  {
    field: 'depreciationPeriodRemooc',
    label: 'depreciationPeriodRemooc',
    headerName: $t('labels.depreciationPeriodRemoocByYear'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.depreciationPeriodRemooc)
    }
  },
  {
    field: 'priceLiquidation',
    label: 'priceLiquidation',
    headerName: $t('labels.priceLiquidation'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.priceLiquidation)
    }
  },
  {
    field: 'depreciationRate',
    label: 'depreciationRate',
    headerName: $t('labels.depreciationRateAmountYear'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      return $formatters.formatCurrency(params.data.depreciationRate)
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'vehicleDataTable',
      storeName: 'VehicleRemoocCategory',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          click: handleViewDetail,
          permissions: checkPermission(layoutPermissions.remooc_category_management),
          isAll: true
        },
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.remooc_category_edit),
          click: handleEditRemoocCategory
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.remooc_category_delete),
          click: handleDeleteRemoocCategory,
          isArchive: true
        }
      ]
    }
  }
]
const handleAddRemoocCategory = () => {
  if (showDrawerAddRemoocCategory.value) {
    showDrawerAddRemoocCategory.value.openAddRemoocCategory()
  }
}

const actionDeleteRemoocCategory = () => {
  const api = $url.vehicle.remoocCategory.delete
  const id = remoocCategoryIsSelectedForDelete.value
  $api(api, { body: { id } }).then((data: any) => {
    const resData: any = data.data.value
    if (resData.success) {
      dataTables.value.refresh()
      showPopupDeleteRemoocCategory.value = false
      process.client && $toast().success(resData.data)
    }
  })
}

const closePopupDeleteRemoocCategory = () => {
  showPopupDeleteRemoocCategory.value = false
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const apiArchive = computed(() => {
  return { api: $url.vehicle.remoocCategory.archive, permissions: checkPermission(layoutPermissions.remooc_category_archive) }
})

const apiRestore = computed(() => {
  return { api: $url.vehicle.remoocCategory.restore, permissions: checkPermission(layoutPermissions.remooc_category_restore) }
})
</script>
